/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import Datatable from '@/components/common/Datatable';
import { Box, Divider, HStack, Icon, Image, Text, VStack } from '@chakra-ui/react';

import EyeIcon from '../../assets/svg/eye.svg?react';
import { useNavigate } from 'react-router-dom';
import { IProduct } from '@/services/manageProduct/types';
import { Column } from 'react-table';
import { useGetProductsWithReviews } from '@/services/reviews';
import { StarIcon } from '@chakra-ui/icons';
import { NAVIGATION_ROUTES } from '@/routes/constants';

export interface IVariant {
  id: number;
  color: string;
  value: string;
  size: string;
  variant_price: number;
  stock_quantity: number;
}

const CustomerReviews = () => {
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<{
    start_date?: string;
    end_date?: string;
    category?: string;
    subcategory?: string;
  }>({
    start_date: '',
    end_date: '',
    category: '',
    subcategory: '',
  });
  const navigate = useNavigate();

  const {
    data: Products,
    refetch,
    isLoading,
    isError,
  } = useGetProductsWithReviews({
    page: pagination.page,
    query,
    ...filters,
  });

  useEffect(() => {
    if (Products) {
      setPagination((prev) => ({
        ...prev,
        pageSize: 10,
        totalCount: Products.count,
        totalPages: Products.page_count,
      }));
    }
  }, [Products, refetch, Products]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const applyFilters = () => {
    refetch();
  };

  const resetFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      category: '',
      subcategory: '',
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    if (
      filters.start_date === '' &&
      filters.end_date === '' &&
      filters.category === '' &&
      filters.subcategory === ''
    ) {
      refetch();
    }
  }, [filters, pagination.page]);

  const columns: Column<IProduct>[] = useMemo(
    () => [
      {
        Header: 'Product ID',
        accessor: 'id',
      },
      {
        Header: 'Product Name',
        accessor: 'name',
        Cell: ({ row }: any) => (
          <HStack gap={2} alignItems={'end'} minWidth={'300px'}>
            <Image
              h={'48px'}
              w={'48px'}
              minW="48px"
              maxW="48px"
              maxH="48px"
              borderRadius={'lg'}
              src={row.original.images?.[0]?.image_small}
              alt={row.original.name}
              objectFit={'cover'}
              objectPosition={'top'}
            />
            <VStack gap={1} align="start" justifyContent="end" h="full">
              <Text
                fontSize="sm"
                fontWeight="medium"
                maxW={{ base: '200px', md: '300px', lg: '400px' }}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                title={row.original.name}
              >
                {row.original.name}
              </Text>
            </VStack>
          </HStack>
        ),
      },

      {
        Header: 'Total Reviews',
        Cell: ({ row }: any) => {
          const total = row.original.total_reviews;
          return total ? (
            <Text minWidth={'100px'}>{total}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Avg. Rating',
        Cell: ({ row }: any) => {
          const averageRating = row.original?.average_rating;
          return averageRating ? (
            <HStack gap={2}>
              <Icon as={StarIcon} color={'brand.secondary.DEFAULT'} />
              <Text>{averageRating}</Text>
            </HStack>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Recommended',
        Cell: ({ row }: any) => {
          const recommendedPercentage = row.original?.recommended_percentage;
          return recommendedPercentage ? (
            <Text>{recommendedPercentage}%</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Actions',
        Cell: ({ row }: any) => {
          return (
            <Icon
              as={EyeIcon}
              boxSize={5}
              cursor={'pointer'}
              onClick={() => {
                navigate(
                  NAVIGATION_ROUTES.REVIEWS_BY_PRODUCT.replace(':reviewsId', row.original.id),
                );
              }}
            />
          );
        },
      },
    ],
    [Products],
  );
  return (
    <Box>
      <Datatable
        data={Products?.results ?? []}
        isLoading={isLoading}
        isError={isError}
        columns={columns}
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        filters={filters}
        setFilters={setFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        sticky={true}
      />
    </Box>
  );
};

export default CustomerReviews;
/* eslint-disable @typescript-eslint/no-explicit-any */
